import { Recipients } from "../types/PersologOnlineTypes";

/**
 * Resolves the profile language to pre-select for a product, honoring, in order: the project's
 * current language, the trainer's default language, the app locale, then English, else "".
 */
export function resolveSelectedLocale(
    profileLanguages: string[] | undefined,
    projectLanguage: string | undefined,
    trainerLanguage: string | undefined,
    appLocale: string,
): string {
    if (profileLanguages) {
        if (projectLanguage && profileLanguages.includes(projectLanguage)) return projectLanguage;
        if (trainerLanguage && profileLanguages.includes(trainerLanguage)) return trainerLanguage;
        if (profileLanguages.includes(appLocale)) return appLocale;
        if (profileLanguages.includes("en")) return "en";
    }
    return "";
}

/**
 * After activation, a trainer-only project may only be kept or widened to "both" — it can still be
 * upgraded.
 */
export function isRecipientUpgradeOnly(
    hasActiveParticipants: boolean,
    originalRecipient: Recipients,
): boolean {
    return hasActiveParticipants && originalRecipient === Recipients.TRAINER;
}

/**
 * After activation, a project that already reaches the participant (PARTICIPANT/BOTH) is locked
 * entirely.
 */
export function isRecipientFullyLocked(
    hasActiveParticipants: boolean,
    originalRecipient: Recipients,
): boolean {
    return hasActiveParticipants && originalRecipient !== Recipients.TRAINER;
}

/**
 * The recipient options that are allowed given the activation state and whether the product has a
 * benchmark diagram (which cannot be sent to the participant directly).
 */
export function allowedRecipients(
    hasActiveParticipants: boolean,
    originalRecipient: Recipients,
    productHasBenchmarkDiagram: boolean,
): Recipients[] {
    return Object.values(Recipients).filter((recipient) => {
        if (isRecipientUpgradeOnly(hasActiveParticipants, originalRecipient)) {
            return recipient === Recipients.TRAINER || recipient === Recipients.BOTH;
        }
        if (productHasBenchmarkDiagram) {
            return recipient !== Recipients.PARTICIPANT;
        }
        return true;
    });
}

/** Coerces a candidate recipient to a valid option from `available`, defaulting to TRAINER. */
export function isSelectable(recipient: string | undefined, available: Recipients[]): Recipients {
    if (recipient && available.includes(recipient as Recipients)) {
        return recipient as Recipients;
    }
    return Recipients.TRAINER;
}

/** The additional-recipient block is complete only when all three fields are filled. */
export function isAdditionalRecipientComplete(
    email: string | undefined,
    firstName: string | undefined,
    lastName: string | undefined,
): boolean {
    return !!email && !!firstName && !!lastName;
}

/**
 * The additional recipient is invalid when its email is malformed, or when the three fields are
 * partially (but not fully) filled. `emailValid` is the caller's email-format check for `email`.
 */
export function isAdditionalRecipientInvalid(
    email: string | undefined,
    firstName: string | undefined,
    lastName: string | undefined,
    emailValid: boolean,
): boolean {
    const filledCount = Number(!!email) + Number(!!firstName) + Number(!!lastName);
    const partiallyFilled = filledCount !== 0 && filledCount !== 3;
    return (!!email && !emailValid) || partiallyFilled;
}
